import { useEffect, useState } from "react";
import { Download, Eye, FileCheck2, Filter, RotateCcw } from "lucide-react";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { Button, DateRange, Modal, Select } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { PreviewAppraisalLetter } from "../../components/features/EmployeeAppraisal/components/preview-appraisal-letter.tsx";
import { exportAppraisals, getAppraisal, searchAppraisals } from "../../components/features/appraisal-workflow/appraisal-api.ts";
import { toAppraisalLetterPreviewData } from "../../components/features/appraisal-workflow/appraisal-annexure-review-data.ts";
import { AppraisalEmployeeAvatar, formatDate, StatusBadge, useAppraisalSearch } from "../../components/features/appraisal-workflow/workflow-shared.tsx";
import type { AppraisalDetail, AppraisalRow } from "../../components/features/appraisal-workflow/types.ts";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import styles from "../../components/features/appraisal-workflow/appraisal-workflow.module.css";

const LETTER_TYPE_OPTIONS = [
  { value: "APPRAISAL", label: "Appraisal Letter" },
  { value: "PROMOTION", label: "Promotion Letter" },
  { value: "APPRAISAL_WITH_PROMOTION", label: "Appraisal with Promotion Letter" },
];

type ApprovedTableRow = AppraisalRow & Record<string, unknown>;
type DraftFilters = { employeeId: string; letterType: string; startDate: Date | null; endDate: Date | null };
const EMPTY_FILTERS: DraftFilters = { employeeId: "", letterType: "", startDate: null, endDate: null };

function localIso(date: Date | null): string {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function letterTypeLabel(value: string): string {
  return LETTER_TYPE_OPTIONS.find((option) => option.value === value)?.label
    ?? value.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function ApprovedAppraisalsPage() {
  const search = useAppraisalSearch("APPROVED");
  const employees = useEmployeeNameOptions();
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(EMPTY_FILTERS);
  const [detail, setDetail] = useState<AppraisalDetail | null>(null);
  const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
  const [approvedCount, setApprovedCount] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const loadApprovedCount = async () => {
    const result = await searchAppraisals({ employeeId: "", status: "APPROVED", letterType: "", fromDate: "", toDate: "", page: 0, size: 1 });
    setApprovedCount(result.totalElements);
  };

  useEffect(() => { if (search.error) showError(search.error); }, [search.error]);
  useEffect(() => { void loadApprovedCount().catch(() => undefined); }, []);

  const viewAnnexure = async (row: AppraisalRow) => {
    setViewLoadingId(row.id);
    try {
      const loaded = await getAppraisal(row.id);
      setDetail({ ...row, ...loaded, id: loaded.id ?? row.id, employeeCode: row.employeeCode, designation: row.designation, employeeType: loaded.employeeType ?? row.employeeType });
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to load the approved appraisal annexure.");
    } finally {
      setViewLoadingId(null);
    }
  };

  const columns: ResponsiveColumn<ApprovedTableRow>[] = [
    {
      id: "employeeName", header: "Employee Name", accessor: "employeeName", sortable: true,
      render: (row) => <div className={styles.employeeCell}><AppraisalEmployeeAvatar employeeName={row.employeeName} photoPath={row.photoPath} /><div className={styles.employeeIdentity}><strong title={row.employeeName}>{row.employeeName}</strong><small>{row.designation || "-"}</small></div></div>,
      getTitleText: (row) => row.employeeName,
    },
    { id: "letterType", header: "Letter Type", accessor: "letterType", sortable: true, render: (row) => <span className={styles.letterBadge}>{letterTypeLabel(row.letterType)}</span>, nowrap: true },
    { id: "effectiveDate", header: "Effective Date", accessor: "effectiveDate", sortable: true, render: (row) => formatDate(row.effectiveDate?.slice(0, 10)), nowrap: true },
    { id: "approvedOn", header: "Approved On", sortable: true, getSortValue: (row) => row.updatedAt ?? row.createdAt ?? "", render: (row) => formatDate(row.updatedAt ?? row.createdAt), nowrap: true },
    { id: "cpoStatus", header: "Status", accessor: "cpoStatus", sortable: true, render: (row) => <StatusBadge status={row.cpoStatus} />, nowrap: true },
    { id: "annexure", header: "Annexure", render: (row) => <Button size="sm" variant="muted-outline" startIcon={<Eye size={15} />} disabled={viewLoadingId != null} onClick={() => void viewAnnexure(row)}>{viewLoadingId === row.id ? "Loading..." : "View Annexure"}</Button>, nowrap: true },
  ];

  const applyFilters = () => search.update({ employeeId: draftFilters.employeeId, letterType: draftFilters.letterType, status: "APPROVED", fromDate: localIso(draftFilters.startDate), toDate: localIso(draftFilters.endDate), page: 0 });
  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    search.update({ employeeId: "", letterType: "", status: "APPROVED", fromDate: "", toDate: "", page: 0 });
  };

  const exportRows = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await exportAppraisals({
        employeeId: search.filters.employeeId,
        status: "APPROVED",
        letterType: search.filters.letterType,
        fromDate: search.filters.fromDate,
        toDate: search.filters.toDate,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSuccess("Approved appraisal export downloaded successfully.");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to export approved appraisals.");
    } finally {
      setExporting(false);
    }
  };

  return <PageShell><div className={`${styles.approvalPage} ${styles.approvalPageClosed}`}>
    <div className={`${styles.approvalSummaryGrid} ${styles.singleSummaryCardGrid}`}>
      <div className={`${styles.summaryCard} ${styles.summaryCardStatic}`}><span className={`${styles.summaryIcon} ${styles.tonegreen}`}><FileCheck2 size={20} /></span><span><strong>Approved</strong><b>{approvedCount ?? "--"}</b></span></div>
    </div>

    <section className={styles.listPanel}>
      <Card className={styles.approvalFiltersCard}><div className={styles.approvalFiltersGrid}>
        <Select label="Employee Name" placeholder={employees.loading ? "Loading Employees..." : "Select Employee Name"} searchable isClearable options={employees.options} value={draftFilters.employeeId} disabled={employees.loading || search.loading} onChange={(employeeId) => setDraftFilters((current) => ({ ...current, employeeId }))} />
        <DateRange label="Effective Date" placeholderText="Select Effective Date" startDate={draftFilters.startDate} endDate={draftFilters.endDate} disabled={search.loading} onChange={([startDate, endDate]) => setDraftFilters((current) => ({ ...current, startDate, endDate }))} />
        <Select label="Letter Type" placeholder="Select Letter Type" isClearable options={LETTER_TYPE_OPTIONS} value={draftFilters.letterType} disabled={search.loading} onChange={(letterType) => setDraftFilters((current) => ({ ...current, letterType }))} />
        <div className={styles.approvalFilterActions}><Button variant="accent" startIcon={<Filter size={16} />} disabled={search.loading} onClick={applyFilters}>Apply Filter</Button><Button variant="light-grey" startIcon={<RotateCcw size={16} />} disabled={search.loading} onClick={clearFilters}>Clear Filter</Button><Button variant="muted-outline" startIcon={<Download size={16} />} disabled={search.loading || exporting} onClick={() => void exportRows()}>{exporting ? "Exporting..." : "Export"}</Button></div>
      </div></Card>

      <ResponsiveDataTable<ApprovedTableRow>
        aria-label="Approved appraisals" columns={columns} data={search.rows as ApprovedTableRow[]}
        page={search.filters.page + 1} pageSize={search.filters.size} totalItems={search.totalElements}
        onPageChange={(page) => search.update({ page: page - 1 })} onPageSizeChange={(size) => search.update({ size, page: 0 })}
        emptyState={<div className={styles.approvalEmptyState}>{search.loading ? "Loading Records..." : "No approved appraisals available"}</div>}
        totalLabel={`Total Records: ${search.totalElements}`}
      />
    </section>

    <Modal open={detail != null} onClose={() => setDetail(null)} title="Approved Appraisal Preview" size="xl" className={styles.approvedPreviewModal} bodyClassName={styles.approvedPreviewModalBody}>
      {detail ? <div className={styles.workflowModalContent}>
        <div className={styles.pendingPreviewLayout}>
          <section className={`${styles.workflowSection} ${styles.managementPreviewPanel} ${styles.approvedPreviewScroll}`}>
            <PreviewAppraisalLetter data={toAppraisalLetterPreviewData(detail)} className={`${styles.workflowLetterPreview} ${styles.approvedLetterPreview}`} fillHeight />
          </section>
        </div>
      </div> : null}
    </Modal>
  </div></PageShell>;
}
